'use client'

import checkmarkIcon from '@public/images/icon-checkmark-rounded.svg'
import Image from 'next/image'
import toast, { Toaster } from 'react-hot-toast'
import useSWRMutation from 'swr/mutation'

import { Button } from '@src/components/Button/Button'
import { Skeleton } from '@src/components/Skeleton/Skeleton'
import { Spinner } from '@src/components/Spinner/Spinner'
import { useConsents } from '@src/hooks/useConsents'
import { useOrderContext } from '@src/hooks/useOrderContext'
import { ServerActions } from '@src/services/server-actions.service'
import { useTrackEnterLeadWheel } from '@src/utils/analytics.util'
import { filterBuilderAttributes } from '@src/utils/builder.util'
import { getHasExistingPartner } from '@src/utils/cart.util'

interface PartnerModuleProps {
  title: string
  description: string
  successLine1: string
  successLine2: string
  ctaText: string
  backgroundImage: string
  backgroundColor: string
  attributes: any
}

export function PartnerModule(props: PartnerModuleProps) {
  const { order } = useOrderContext()
  const consents = useConsents()
  const trackEnterLeadWheel = useTrackEnterLeadWheel()
  const hasExistingPartner = getHasExistingPartner(order)
  const hasPartner = !!order?.custom?.fields?.partnerId

  const enterLeadWheel = useSWRMutation('enterLeadWheel', ServerActions.serial.enterLeadWheel, {
    onError() {
      toast.error('Something went wrong. Please try again.')
    },
  })

  if (hasExistingPartner || hasPartner) return null

  return (
    <div {...filterBuilderAttributes(props.attributes)} data-testid="partner-module">
      <div
        className="relative min-h-[511px] rounded-2xl px-6 py-8 md:min-h-[290px] md:p-16"
        style={{ backgroundColor: props.backgroundColor }}
      >
        <div
          className="absolute inset-x-0 bottom-0 top-1/2 rounded-b-2xl bg-cover bg-right-bottom bg-no-repeat md:inset-0 md:rounded-2xl md:bg-cover md:bg-center"
          style={{
            backgroundImage: props.backgroundImage
              ? `url(${props.backgroundImage})`
              : 'url(/images/partner-module-bg.png)',
          }}
        />
        <div className="relative mx-auto flex max-w-md flex-col justify-center text-center md:mx-0 md:justify-start md:text-left">
          <div className="text-heading-display-3 px-20 md:px-0">{props.title}</div>
          {enterLeadWheel.data ? (
            <div className="text-body-md">
              <Image
                className="mx-auto mb-2 mt-4 size-8 md:mb-3 md:ml-0 md:mt-1"
                alt="checkmark-icon"
                src={checkmarkIcon}
              />
              <div className="font-semibold">{props.successLine1}</div>
              <div>{props.successLine2}</div>
            </div>
          ) : (
            <>
              <div className="mt-3 px-10 text-body-md md:px-0">{props.description}</div>
              <div className="mt-5">
                <Button
                  variant="secondary"
                  size="small"
                  isLoading={enterLeadWheel.isMutating}
                  loadingComponent={
                    <Spinner
                      size="small"
                      className="fill-bodi-green-deep-500 text-bodi-gray-light"
                    />
                  }
                  className="h-10 w-56"
                  onClick={() => {
                    if (enterLeadWheel.isMutating) return
                    trackEnterLeadWheel()
                    void enterLeadWheel.trigger().then(() => {
                      void consents.sendConsent({
                        email: String(order?.customerEmail),
                        slug: 'partner',
                        value: true,
                      })
                    })
                  }}
                  data-testid="partner-module-button"
                >
                  {props.ctaText}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mx-auto mt-5 w-full max-w-3xl text-center text-body-xs">
        {consents.isLoading ? (
          <Skeleton.Text
            text="Only your contact and product information will be made available to your free BODi
                Partner who may reach out to help support you with your health and wellness goals. You
                can stop sharing your contact information at any time."
          />
        ) : consents.slugs.partner?.consentText ? (
          `*${consents.slugs.partner.consentText}`
        ) : null}
      </div>
      <Toaster />
    </div>
  )
}
